import { PromiseHelper } from '../../../utils/PromiseHelper';
import { EventsInterfaceType } from '@clientos/types';
import { Refreshable } from '../../../utils/refreshable/IRefreshable';
import { IContainer } from '../../../core/containers';
import { IAuthTokenService } from '../../../services/authTokenService';
import { EntitlementRepository } from '../../../services/entitlements/EntitlementRepository';
import EventNames from '../../../config/eventNames';
import getProgramInfo from '../../../clients/stratus/entitlements/getProgramInfo';
import getUserWithTenantDetails from '../../../clients/stratus/usersMgt/getUserWithTenantDetails';
import {
  EntitlementsListType,
  ProgramInfoMapPropertyType,
  SubscriptionOfferingListType,
  ProgramInfoResponseType
} from '../../../clients/stratus/entitlements/getProgramInfo/types';
import { UserContextEnum } from '../../types';
import {
  AuthProviderV2Type,
  OfferingAndEntitlementType,
  EntitlementsDetailsType,
  RefreshType,
  OfferingAndEntitlementOptionsType,
  EntitlementsInterfaceType
} from './types';
import { internalLogger } from '../clientOsLogger';
import userContextEnumToAuthContextEnum from '../../../services/authTokenService/utils/userContextEnumToAuthContextEnum';
import bindAllMethods from '../../../utils/bindAllMethods';
import {
  getContainers,
  getRepositories,
  getServices
} from '../../../infra/startup';

export type EntitlementsInterfaceInitType = {
  authProvider: AuthProviderV2Type;
  entitlementsInput: any;
  events: EventsInterfaceType;
};

export class EntitlementsInterface extends Refreshable {
  private defaultKey = 'default';
  private initializedUserContexts: Map<
    UserContextEnum | typeof this.defaultKey,
    boolean
  > = new Map();
  private enabled = false;

  private promiseHelper;
  private stack;
  private authProvider: AuthProviderV2Type;
  private events: EventsInterfaceType;
  private accessControlContainer: IContainer;
  private authTokenService: IAuthTokenService;
  private repository: EntitlementRepository;

  constructor({
    events,
    entitlementsInput,
    authProvider
  }: EntitlementsInterfaceInitType) {
    super();
    const services = getServices();
    const containers = getContainers();
    const repositories = getRepositories();
    const { enabled, stack } = entitlementsInput || {};
    this.enabled = enabled;
    this.stack = stack;
    this.promiseHelper = new PromiseHelper();

    // Sadly this is still using interfaces
    this.authProvider = authProvider;
    this.events = events;

    this.authTokenService = services.authTokenService;

    this.accessControlContainer = containers.accessControlContainer;

    this.repository = repositories.entitlementRepository;

    bindAllMethods(this);

    // new schema
    this.setRefreshMethods({
      init: this._initialize.bind(this),
      refresh: this.refresh.bind(this)
    });

    // old schema
    this.accessControlContainer.addInitServices(
      this._initialize,
      undefined,
      'EntitlementsInterface'
    );
    this.accessControlContainer.addRefreshServices(
      this.refresh,
      undefined,
      'EntitlementsInterface'
    );
  }

  getInterface(): EntitlementsInterfaceType {
    return {
      refresh: this.refresh,
      checkEntitlements: this.checkEntitlements,
      checkOfferings: this.checkOfferings,
      getPlanInfo: this.getPlanInfo,
      getOffering: this.getOffering,
      getEntitlement: this.getEntitlement
    };
  }

  private _isDifferentFromCache(
    newProgramInfo: ProgramInfoResponseType,
    authContextSuffix: string
  ) {
    try {
      const cachedProgramInfo: ProgramInfoResponseType =
        this.repository.findOne(authContextSuffix);

      const newProgramInfoString = JSON.stringify(newProgramInfo);
      const cachedProgramInfoString = JSON.stringify(cachedProgramInfo);

      return newProgramInfoString !== cachedProgramInfoString;
    } catch (err) {
      internalLogger?.error?.(err);
      return true;
    }
  }

  private _getAuthProviderByUserContextEnum(
    userContextTypeString: string
  ): AuthProviderV2Type {
    const userContext = userContextTypeString
      ? UserContextEnum[userContextTypeString]
      : null;
    return this.authProvider.createAuthProviderByUserContextEnum(userContext);
  }

  private _getAuthContextSuffix(options?: OfferingAndEntitlementOptionsType) {
    const authContext = options?.userContext
      ? userContextEnumToAuthContextEnum(options.userContext)
      : this.authTokenService.getCurrentContext();
    return this.authTokenService.getSuffix(authContext);
  }

  private async refreshAction(options?: OfferingAndEntitlementOptionsType) {
    const _authProvider = this._getAuthProviderByUserContextEnum(
      options?.userContext
    );

    const userAndTenantDetailsResponse = await getUserWithTenantDetails({
      authProvider: _authProvider,
      stack: this.stack
    });

    const tenantResourceId =
      userAndTenantDetailsResponse?.data?.userTenantDetail?.tenantResourceId;
    const userResourceId = userAndTenantDetailsResponse?.data?.user?.resourceId;

    const programInfoDetailsResponse = await getProgramInfo({
      authProvider: _authProvider,
      stack: this.stack,
      tenantResourceId,
      userResourceId
    });

    const newProgramInfo =
      programInfoDetailsResponse?.data || ({} as ProgramInfoResponseType);

    const authContextSuffix = this._getAuthContextSuffix(options);

    if (this._isDifferentFromCache(newProgramInfo, authContextSuffix)) {
      this.repository.save(authContextSuffix, newProgramInfo);
      this.events.triggerEvent(
        EventNames.shellEntitlementChangedEventName,
        undefined
      );
    }
  }

  async refresh(
    refreshOption?: RefreshType,
    options?: OfferingAndEntitlementOptionsType
  ): Promise<void> {
    const promiseKey = options?.userContext ?? this.defaultKey;
    let promise = this.promiseHelper.getPendingPromise(promiseKey);
    if (this.isEnabled() && !promise) {
      promise = this.refreshAction(options);
      this.promiseHelper.setPendingPromise(promiseKey, promise);
    }
    await promise;
  }

  private async _initialize(options?: OfferingAndEntitlementOptionsType) {
    const contextKey = options?.userContext ?? this.defaultKey;
    let promise = this.promiseHelper.getPendingPromise(contextKey);
    const isInitialized = this.initializedUserContexts.get(contextKey);

    if (!isInitialized) {
      this.initializedUserContexts.set(contextKey, true);
      if (!promise) {
        promise = this.refresh({ reloadContent: false }, options);
        this.promiseHelper.setPendingPromise(contextKey, promise);
      }
    }

    await promise;
  }

  private getFlatMap<key extends keyof ProgramInfoMapPropertyType>(
    propertyName: key,
    authContextSuffix: string
  ) {
    const programInfo: EntitlementsDetailsType =
      this.repository.findOne(authContextSuffix);

    let flatmap: ProgramInfoMapPropertyType[typeof propertyName] = [];

    if (typeof programInfo?.programInfoMap === 'object') {
      Object.values(programInfo?.programInfoMap).forEach((value) => {
        if (value && value[propertyName]) {
          flatmap = [
            ...flatmap,
            ...value[propertyName]
          ] as ProgramInfoMapPropertyType[typeof propertyName];
        }
      });
    }
    return flatmap.reverse() as ProgramInfoMapPropertyType[typeof propertyName];
  }

  async checkEntitlements(
    entitlements: OfferingAndEntitlementType[],
    options?: OfferingAndEntitlementOptionsType
  ): Promise<boolean> {
    if (!this.isEnabled()) return true;

    await this._initialize(options);

    const entitlementAndOfferingKey = this._getAuthContextSuffix(options);

    const flatMapEntitlements = this.getFlatMap(
      'entitlementList',
      entitlementAndOfferingKey
    );
    const testEntitlements = entitlements?.every?.(
      (argumentEntitlement) =>
        !!flatMapEntitlements?.find?.(
          (cachedEntitlement) =>
            cachedEntitlement?.state == argumentEntitlement?.state &&
            cachedEntitlement?.serviceId == argumentEntitlement?.serviceId
        )
    );
    return !!testEntitlements;
  }

  async checkOfferings(
    offerings: OfferingAndEntitlementType[],
    options?: OfferingAndEntitlementOptionsType
  ): Promise<boolean> {
    if (!this.isEnabled()) return true;

    await this._initialize(options);

    const entitlementAndOfferingKey = this._getAuthContextSuffix(options);

    const flatMapOfferings = this.getFlatMap(
      'subscriptionOfferingList',
      entitlementAndOfferingKey
    );
    const testOffering = offerings?.every?.(
      (argumentOffering) =>
        !!flatMapOfferings?.find?.(
          (cachedOffering) =>
            cachedOffering?.state == argumentOffering?.state &&
            cachedOffering?.serviceId == argumentOffering?.serviceId
        )
    );
    return !!testOffering;
  }

  async getPlanInfo(options?: OfferingAndEntitlementOptionsType) {
    if (!this.isEnabled()) return undefined;

    await this._initialize(options);

    const authContextSuffix = this._getAuthContextSuffix(options);

    const programInfo: EntitlementsDetailsType =
      this.repository.findOne(authContextSuffix);
    return programInfo?.planInfo;
  }

  async getOffering(
    serviceId: string,
    options?: OfferingAndEntitlementOptionsType
  ): Promise<SubscriptionOfferingListType> {
    if (!this.isEnabled()) return undefined;

    await this._initialize(options);

    const entitlementAndOfferingKey = this._getAuthContextSuffix(options);

    const flatOfferingMap = this.getFlatMap(
      'subscriptionOfferingList',
      entitlementAndOfferingKey
    );
    return flatOfferingMap?.find?.(
      (offering) => offering?.serviceId === serviceId
    );
  }

  async getEntitlement(
    serviceId: string,
    options?: OfferingAndEntitlementOptionsType
  ): Promise<EntitlementsListType> {
    if (!this.isEnabled()) return undefined;

    await this._initialize(options);

    const entitlementAndOfferingKey = this._getAuthContextSuffix(options);

    const flatEntitlememtsMap = this.getFlatMap(
      'entitlementList',
      entitlementAndOfferingKey
    );
    return flatEntitlememtsMap?.find?.(
      (entitlement) => entitlement?.serviceId === serviceId
    );
  }

  public isEnabled(): boolean {
    if (!this.enabled) {
      console.warn('Entitlements: disabled');
      return false;
    }
    return true;
  }
}

export function createNoopEntitlementsInterface(): EntitlementsInterfaceType {
  return {
    refresh: function (): any {
      /* istanbul ignore next */
      internalLogger?.debug?.d5({
        className: 'createNoopEntitlementsInterface',
        methodName: 'refresh()',
        message: 'Noop interface method called - no operation performed'
      });
    },
    checkEntitlements: function (): any {
      /* istanbul ignore next */
      internalLogger?.debug?.d5({
        className: 'createNoopEntitlementsInterface',
        methodName: 'checkEntitlements()',
        message: 'Noop interface method called - no operation performed'
      });
    },
    checkOfferings: function (): any {
      /* istanbul ignore next */
      internalLogger?.debug?.d5({
        className: 'createNoopEntitlementsInterface',
        methodName: 'checkOfferings()',
        message: 'Noop interface method called - no operation performed'
      });
    },
    getPlanInfo: function (): any {
      /* istanbul ignore next */
      internalLogger?.debug?.d5({
        className: 'createNoopEntitlementsInterface',
        methodName: 'getPlanInfo()',
        message: 'Noop interface method called - no operation performed'
      });
    },
    getOffering: function (): any {
      /* istanbul ignore next */
      internalLogger?.debug?.d5({
        className: 'createNoopEntitlementsInterface',
        methodName: 'getOffering()',
        message: 'Noop interface method called - no operation performed'
      });
    },
    getEntitlement: function (): Promise<any> {
      /* istanbul ignore next */
      internalLogger?.debug?.d5({
        className: 'createNoopEntitlementsInterface',
        methodName: 'getEntitlement()',
        message: 'Noop interface method called - returning resolved promise'
      });
      return Promise.resolve();
    }
  };
}
